// src/components/admin/pagos/planEditor/EntregablesSection.js
import React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Input, Label, Combobox } from '../../../ui'

// Un entregable = algo que se le da al jugador y la condicion para recibirlo. Hay dos
// reglas y no son intercambiables:
//
//   'concepto'    -> se libera al pagar ESA prenda. Es la de un uniforme del plan, que
//                    ya tiene su propio cobro. Viene puesta sola.
//   'parcialidad' -> se libera "al llegar al Pago N". Es la de lo que NO tiene cobro
//                    propio: una credencial, una chamarra de porra.
const QUE_OTRO = '__otro__'
const CUANDO_CONCEPTO = '__concepto__'

const EntregablesSection = ({ entregables = [], parcialidades = [], conceptosEntregables = [], onChange }) => {
  // Un buscador que dice "Buscar uniforme..." sobre una lista sin un solo uniforme
  // parece una lista rota, no una lista vacia. Cuando el plan no tiene prendas, el
  // desplegable lo dice ahi mismo -- donde el usuario se hace la pregunta -- y no
  // solo en la nota al pie, que se lee despues o nunca.
  const sinUniformes = conceptosEntregables.length === 0
  const conceptosDeUniforme = new Set(conceptosEntregables.map((c) => c.value))

  // Las parcialidades de uniforme NO son umbrales validos. El umbral de la regla
  // 'parcialidad' es ACUMULADO (todo lo anterior mas ese pago), y como los uniformes
  // se agregan al final del calendario, elegir "Jersey" como condicion exigia el plan
  // casi completo -- justo el error que este rediseno vino a quitar.
  const pagoOptions = parcialidades
    .filter((p) => !p.concepto_id || !conceptosDeUniforme.has(p.concepto_id))
    .map((p) => ({
      value: String(p.orden),
      label: p.fecha_limite ? `${p.nombre} · ${p.fecha_limite}` : p.nombre,
    }))
  const ultimoPago = pagoOptions.length ? Number(pagoOptions[pagoOptions.length - 1].value) : 1
  const queOptions = [...conceptosEntregables, { value: QUE_OTRO, label: 'Otro (escribir)' }]

  // La regla 'concepto' se pregunta "¿pagaron ESTA prenda?" mirando SU parcialidad. Si
  // la prenda no tiene una -- planes viejos, donde el uniforme va disuelto en las
  // mensualidades -- esa regla no encuentra nada y el uniforme NO SE LIBERA NUNCA. En
  // esos planes la unica condicion que funciona es "al llegar al Pago N".
  const tieneCobroPropio = (conceptoId) => parcialidades.some((p) => p.concepto_id === conceptoId)

  const update = (i, patch) => onChange(entregables.map((e, idx) => idx === i ? { ...e, ...patch } : e))
  const add = () => onChange([...entregables, { nombre: '', concepto_id: null, regla_tipo: 'parcialidad', hasta_orden: ultimoPago }])
  const remove = (i) => onChange(entregables.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Entregables</Label>
        <Button type="button" variant="outline" size="sm" onClick={add}><Plus className="mr-1 h-4 w-4" /> Entregable</Button>
      </div>

      {entregables.length === 0 ? (
        <p className="rounded-lg border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
          Opcional. Agrega lo que entregas al jugador (un uniforme del plan, una credencial…) y cuándo se entrega.
        </p>
      ) : (
        <div className="space-y-2">
          {entregables.map((e, i) => {
            const queValue = e.concepto_id ? e.concepto_id : (e.nombre ? QUE_OTRO : '')
            const esLibre = queValue === QUE_OTRO
            const esUniforme = !!e.concepto_id && conceptosDeUniforme.has(e.concepto_id)
            // "Al pagar esa prenda" solo tiene sentido si la prenda tiene cobro propio.
            const puedeReglaPropia = esUniforme && tieneCobroPropio(e.concepto_id)
            const cuandoOptions = puedeReglaPropia
              ? [{ value: CUANDO_CONCEPTO, label: `Al pagar ${e.nombre || 'esta prenda'}` }, ...pagoOptions]
              : pagoOptions
            const cuandoValue = e.regla_tipo === 'concepto' ? CUANDO_CONCEPTO : String(e.hasta_orden || '')
            return (
              <div key={i} className="space-y-2 rounded-lg border p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">¿Qué se entrega?</Label>
                    <Combobox
                      className="h-9 w-full"
                      options={queOptions}
                      value={queValue}
                      onValueChange={(val) => {
                        // Cambiar QUE cambia que reglas son posibles: una cosa libre no
                        // tiene cobro propio, asi que no puede liberarse "al pagarla".
                        if (val === QUE_OTRO) update(i, { concepto_id: null, regla_tipo: 'parcialidad', hasta_orden: e.hasta_orden || ultimoPago })
                        else {
                          const opt = conceptosEntregables.find((o) => o.value === val)
                          // Sin cobro propio, "al pagar esta prenda" no se cumpliria nunca.
                          const propia = tieneCobroPropio(val)
                          update(i, {
                            concepto_id: val,
                            nombre: opt?.label || '',
                            regla_tipo: propia ? 'concepto' : 'parcialidad',
                            hasta_orden: propia ? null : (e.hasta_orden || ultimoPago),
                          })
                        }
                      }}
                      searchPlaceholder={sinUniformes ? 'Escribe qué entregas...' : 'Buscar uniforme...'}
                      placeholder={sinUniformes ? "Elige 'Otro' y escríbelo" : "Elige un uniforme o 'Otro'"}
                      emptyMessage="Ningún uniforme coincide."
                    />
                    {sinUniformes && (
                      <p className="text-[11px] text-muted-foreground">
                        Este plan no tiene uniformes. Para elegir uno, agrégalo en el
                        paso Conceptos.
                      </p>
                    )}
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 shrink-0 p-0 text-muted-foreground hover:text-destructive" onClick={() => remove(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {esLibre && (
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input className="h-9" value={e.nombre} onChange={(ev) => update(i, { nombre: ev.target.value })} placeholder="ej. Credencial" />
                  </div>
                )}

                <div className="space-y-1">
                  <Label className="text-xs">¿Cuándo se entrega?</Label>
                  <Combobox
                    className="h-9 w-full"
                    options={cuandoOptions}
                    value={cuandoValue}
                    onValueChange={(val) => {
                      if (val === CUANDO_CONCEPTO) update(i, { regla_tipo: 'concepto', hasta_orden: null })
                      else update(i, { regla_tipo: 'parcialidad', hasta_orden: Number(val) })
                    }}
                    searchPlaceholder="Buscar pago..."
                    placeholder="Al llegar al pago…"
                  />
                  {puedeReglaPropia && e.regla_tipo === 'concepto' && (
                    <p className="text-[11px] text-muted-foreground">
                      Se libera cuando la familia pague este uniforme. Si prefieres
                      atarlo a una mensualidad, elígela arriba.
                    </p>
                  )}
                  {esUniforme && !puedeReglaPropia && (
                    <p className="text-[11px] text-muted-foreground">
                      Este uniforme va dentro del total del plan, sin cobro propio, así
                      que su entrega tiene que amarrarse a un pago.
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

export default EntregablesSection
